"use client"

import React from 'react'
import { AlertCircle, MailCheck, RefreshCw } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { api, ApiError } from '@/lib/api'

interface ReactivationPromptProps {
  email: string
  password: string
  message?: string
  onCancel: () => void
}

interface ReactivateResponse {
  success: boolean
  message?: string
  data?: {
    email?: string
    expiresAt?: string
  }
  error?: string
  code?: string
}

export function ReactivationPrompt({
  email,
  password,
  message,
  onCancel,
}: ReactivationPromptProps) {
  const [status, setStatus] = React.useState<'idle' | 'loading' | 'success'>('idle')
  const [error, setError] = React.useState<string | null>(null)
  const [successData, setSuccessData] = React.useState<{ message?: string; expiresAt?: string } | null>(null)

  const handleReactivate = async () => {
    setStatus('loading')
    setError(null)

    try {
      const response = await api.post<ReactivateResponse>('/auth/reactivate', {
        email,
        password,
      })

      if (!response.success) {
        throw new Error(response.error || response.message || 'Failed to reactivate account')
      }

      setSuccessData({
        message: response.message,
        expiresAt: response.data?.expiresAt,
      })
      setStatus('success')
    } catch (err) {
      console.error('Reactivation error:', err)
      if (err instanceof ApiError) {
        try {
          const payload = JSON.parse(err.message)
          setError(payload?.message || payload?.error || 'Failed to reactivate account')
          return
        } catch (parseError) {
          console.warn('Unable to parse reactivation API error payload', parseError)
        }
      }

      setError(err instanceof Error ? err.message : 'Failed to reactivate account')
      setStatus('idle')
    }
  }

  const isLoading = status === 'loading'
  const isSuccess = status === 'success'
  const formattedExpiry = React.useMemo(() => {
    if (!successData?.expiresAt) return null

    const date = new Date(successData.expiresAt)
    if (Number.isNaN(date.getTime())) {
      return null
    }

    return date.toLocaleString()
  }, [successData?.expiresAt])

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 bg-yellow-100">
            {isSuccess ? (
              <MailCheck className="h-6 w-6 text-green-600" />
            ) : (
              <AlertCircle className="h-6 w-6 text-yellow-600" />
            )}
          </div>
          <CardTitle>{isSuccess ? 'Check your email' : 'Account Deactivated'}</CardTitle>
          <CardDescription>
            {isSuccess
              ? `We sent a reactivation link to ${email}.`
              : message || 'Your account has been deactivated. Would you like to reactivate it?'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && !isSuccess && (
            <div className="bg-destructive/5 border border-destructive/15 rounded-lg p-3">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {isSuccess ? (
            <div className="space-y-4 text-left">
              <div className="text-sm text-muted-foreground space-y-2">
                <p>
                  Follow the link in your email to confirm reactivation. We'll unlock your account and sign you in automatically once you confirm.
                </p>
                <p>
                  <strong>Email:</strong> {email}
                </p>
                {formattedExpiry && (
                  <p>Link expires on {formattedExpiry}.</p>
                )}
                {successData?.message && <p>{successData.message}</p>}
              </div>

              <div className="pt-2">
                <Button onClick={onCancel} className="w-full">
                  Back to login
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="text-sm text-muted-foreground">
                <p>
                  <strong>Email:</strong> {email}
                </p>
                <p className="mt-2">Reactivating your account will restore access to your organization and data.</p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={onCancel}
                  disabled={isLoading}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button onClick={handleReactivate} disabled={isLoading} className="flex-1">
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Send Reactivation Email
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
